<script lang="ts">
    import { Roarr as log } from "roarr";

    import TileBoard from "../components/TileBoard.svelte";
    import ChatBox from "../components/ChatBox.svelte";
    import type { GridStackEvent } from "../components/GridStackBoard.svelte";
    import { Colour, TileType } from "../types/tile";
    import type { GridStackTile } from "../types/tile";
    import type { ChatMessage } from "../types/chat";

    import { convertNameToColour } from "../misc/nameToColour";
    import { name } from "../store/userdata";
    import { getColourClass } from "../styles/nameColourClasses";
    import {
        successToast,
        infoToast,
        warningToast,
        errorToast,
    } from "../misc/toasts";

    import { sortByColour, sortByTileType } from "../misc/tileSorters";
    import { navigateTo } from "../misc/routes";

    let tableTiles: GridStackTile[] = [
        {
            x: 0,
            y: 0,
            colour: Colour.RED,
            tileType: TileType.ONE,
            staging: false,
        },
        {
            x: 1,
            y: 0,
            colour: Colour.BLUE,
            tileType: TileType.TWO,
            staging: false,
        },
    ];
    let trayTiles: GridStackTile[] = [];
    let messages: ChatMessage[] = [];

    let moveTableTilesEnabled = true;
    let addTilesEnabled = true;
    let removeTilesEnabled = true;
    let promiseDelayEnabled = false;

    let enableTableTileMovement = true;

    $: nameColourClass = getColourClass(convertNameToColour($name));

    let canMoveTableTile = async (e: GridStackEvent) => {
        if (promiseDelayEnabled) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }

        return moveTableTilesEnabled;
    };

    let canAddTile = async (e: GridStackEvent) => {
        if (promiseDelayEnabled) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }

        return addTilesEnabled;
    };

    let canRemoveTile = async (e: GridStackEvent) => {
        if (promiseDelayEnabled) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
        }

        return removeTilesEnabled;
    };

    let addTableTile = () => {
        tableTiles = [...tableTiles, generateTile()];
    };

    let addTrayTile = () => {
        trayTiles = [...trayTiles, generateTile()];
    };

    let generateTile = () => {
        const colour = Math.floor(Math.random() * 4) as Colour;
        const tileType = Math.floor(Math.random() * 13) as TileType;

        const newTile = {
            x: tableTiles.length + trayTiles.length,
            y: 0,
            colour,
            tileType,
            staging: false,
        };

        return newTile;
    };

    let removeTableTile = () => {
        tableTiles = tableTiles.slice(0, -1);
    };

    let removeTrayTile = () => {
        trayTiles = trayTiles.slice(0, -1);
    };

    let translateTableTiles = (displacement) => {
        tableTiles = tableTiles.map((tile) => {
            return {
                ...tile,
                x: tile.x + displacement,
            };
        });
    };

    let translateTrayTiles = (displacement) => {
        trayTiles = trayTiles.map((tile) => {
            return {
                ...tile,
                x: tile.x + displacement,
            };
        });
    };

    let sendLog = () => {
        log.trace(this, "trace");
        log.debug(this, "debug");
        log.info(this, "info");
        log.warn(this, "warn");
        log.error(this, "error");
        log.fatal(this, "fatal");
    };

    const onBackClick = () => {
        navigateTo("/");
    };
</script>

<div id="game">
    <TileBoard
        bind:tableTiles
        bind:trayTiles
        {canMoveTableTile}
        {canAddTile}
        {canRemoveTile}
        {enableTableTileMovement}
    />
    <ChatBox bind:messages />
</div>

<fieldset style="display: inline; width: 15%">
    <legend>Table</legend>
    <button on:click={addTableTile}>Add</button>
    <button on:click={removeTableTile}>Remove</button>
    <button on:click={() => translateTableTiles(-1)}>LEFT</button>
    <button on:click={() => translateTableTiles(1)}>RIGHT</button>
</fieldset>

<fieldset style="display: inline;width: 15%">
    <legend>Tray</legend>
    <button on:click={addTrayTile}>Add</button>
    <button on:click={removeTrayTile}>Remove</button>
    <button on:click={() => translateTrayTiles(-1)}>LEFT</button>
    <button on:click={() => translateTrayTiles(1)}>RIGHT</button>
</fieldset>

<fieldset style="display: inline;width: 20%">
    <legend>Options</legend>
    <div>
        <input
            type="checkbox"
            name="enableTableTileMovement"
            bind:checked={enableTableTileMovement}
        />
        <label
            for="enableTableTileMovement"
        >{enableTableTileMovement ? 'Table tile movement is enabled' : 'Table tile movement is disabled'}</label>
    </div>
    <div>
        <input
            type="checkbox"
            name="moveTableTilesEnabled"
            bind:checked={moveTableTilesEnabled}
        />
        <label
            for="moveTableTilesEnabled"
        >{moveTableTilesEnabled ? 'You are allowed to move tiles on the table' : 'You are not allowed to move tiles on the table'}</label>
    </div>
    <div>
        <input
            type="checkbox"
            name="addTilesEnabled"
            bind:checked={addTilesEnabled}
        />
        <label
            for="addTilesEnabled"
        >{addTilesEnabled ? 'You are allowed to add tiles from tray to table' : 'You are not allowed to add tiles from tray to table'}</label>
    </div>
    <div>
        <input
            type="checkbox"
            name="removeTilesEnabled"
            bind:checked={removeTilesEnabled}
        />
        <label
            for="removeTilesEnabled"
        >{removeTilesEnabled ? 'You are allowed to remove tiles from table to tray' : 'You are not allowed to remove tiles from table to tray'}</label>
    </div>
    <div>
        <input
            type="checkbox"
            name="promiseDelay"
            bind:checked={promiseDelayEnabled}
        />
        <label
            for="promiseDelay"
        >{promiseDelayEnabled ? 'There is a delay to event promises' : 'There is no delay to event promises'}</label>
    </div>
</fieldset>

<fieldset style="display: inline;width: 15%">
    <legend>Variables</legend>
    <label for="name" class={nameColourClass}>Name:</label>
    <input type="text" name="name" placeholder="Matt" bind:value={$name} />
</fieldset>

<fieldset style="display: inline;width: 15%">
    <legend>Logging</legend>
    <button on:click={() => log.trace('trace')}>Trace log</button>
    <button on:click={() => log.debug('debug')}>Debug log</button>
    <button on:click={() => log.info('info')}>Info log</button>
    <button on:click={() => log.warn('warn')} class="yellow">Warn log</button>
    <button on:click={() => log.error('error')} class="red">Error log</button>
</fieldset>

<fieldset style="display: inline;width: 15%">
    <legend>Toasts</legend>
    <button on:click={() => successToast('Success!')}>Success</button>
    <button on:click={() => infoToast('Info?')}>Info</button>
    <button on:click={() => warningToast('Warning.')}>Warning</button>
    <button on:click={() => errorToast('Error...')}>Error</button>
</fieldset>

<fieldset style="display: inline;width: 15%">
    <legend>Tray sorting</legend>
    <button on:click={() => (trayTiles = sortByColour(trayTiles))}>Sort by
        colour</button>
    <button on:click={() => (trayTiles = sortByTileType(trayTiles))}>Sort by
        tile type</button>
</fieldset>

<fieldset style="display: inline;width: 15%">
    <legend>Navigation</legend>
    <button on:click={onBackClick}>Back</button>
</fieldset>

<style>
    #game {
        display: grid;
        grid-template-columns: 70% 30%;
        gap: 10px;
    }

    label {
        display: inline;
    }

    .yellow {
        background-color: #ffa;
        color: #aa0;
    }

    .red {
        background-color: #faa;
        color: #a00;
    }
</style>
